import {useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {useDialog} from '@/hooks/useDialog'
import {Loader2, Check, X, AlertTriangle, Eye} from 'lucide-react'
import {useI18n} from '@/i18n'
import {SmallText} from '@/components/shared/SmallText'
import {ErrorText} from '@/components/shared/ErrorText'
import {routes} from '@/lib/routes'
import {AppDate, formatTimestamp} from '@/lib/date-utils'
import {Button} from '@/components/ui/button'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {EmptyState} from '@/components/shared/EmptyState'
import {ErrorBanner} from '@/components/ui/error-banner'
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import {useSyncRuns, useSyncRunDetail, useSyncRecords} from '@/features/inbound-connectors/hooks/useSyncRunQueries'
import {useApprovePausedSync, useRejectPausedSync} from '@/features/inbound-connectors/hooks/useSyncRunMutations'
import {useInboundConnectorPermissions} from '@/features/inbound-connectors/hooks/useInboundConnectorPermissions'
import {
    SyncRunStatus,
    SyncType,
    SyncRecordAction,
} from '@/types/inbound-connector'
import type {ErrorLogRef, InboundSyncRun, InboundSyncRecord} from '@/types/inbound-connector'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Pagination} from '@/components/ui/pagination'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {CodeText} from '@/components/shared/CodeText'
import {MutedText} from '@/components/shared/MutedText'
import {DetailField} from '@/components/shared/DetailField'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {TextLink} from '@/components/shared/TextLink'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {StatCard} from '@/components/shared/StatCard'

const RECORDS_FILTER_ALL = 'ALL'

export const SYNC_RUNS_PAGE_PARAM = 'page'
export const SYNC_RUNS_PAGE_SIZE_PARAM = 'size'

export function SyncRunsTab({connectorId}: {connectorId: string}) {
    const i18n = useI18n()
    const {canApproveSync} = useInboundConnectorPermissions()
    const [searchParams, setSearchParams] = useSearchParams()
    const page = parsePositiveInt(searchParams.get(SYNC_RUNS_PAGE_PARAM), 0)
    const pageSize = parsePositiveInt(searchParams.get(SYNC_RUNS_PAGE_SIZE_PARAM), 20)

    const setPage = (next: number) => {
        const updated = new URLSearchParams(searchParams)
        if (next === 0) {
            updated.delete(SYNC_RUNS_PAGE_PARAM)
        } else {
            updated.set(SYNC_RUNS_PAGE_PARAM, String(next))
        }
        setSearchParams(updated, {replace: true})
    }
    const setPageSize = (next: number) => {
        const updated = new URLSearchParams(searchParams)
        if (next === 20) {
            updated.delete(SYNC_RUNS_PAGE_SIZE_PARAM)
        } else {
            updated.set(SYNC_RUNS_PAGE_SIZE_PARAM, String(next))
        }
        updated.delete(SYNC_RUNS_PAGE_PARAM)
        setSearchParams(updated, {replace: true})
    }

    /* eslint-disable i18next/no-literal-string -- query params */
    const queryParams = `page=${page}&size=${pageSize}&sort=startedAt,desc`
    /* eslint-enable i18next/no-literal-string */

    const {data: runsPage, isLoading, error} = useSyncRuns(connectorId, queryParams)

    if (isLoading) {
        return (
            <MutedText className="mt-4" data-testid="sync-runs-loading">{i18n.common.loading}</MutedText>
        )
    }

    if (error) {
        return (
            <Stack className="mt-4">
                <ErrorBanner error={error as Error}/>
            </Stack>
        )
    }

    const runs = runsPage?.content ?? []

    if (runs.length === 0 && page === 0) {
        return (
            <Card className="mt-4">
                <CardContent>
                    <EmptyState
                        icon={Eye}
                        title={i18n.inboundConnectors.syncRunsEmpty}
                        data-testid="sync-runs-empty"
                    />
                </CardContent>
            </Card>
        )
    }

    return (
        <Stack className="mt-4">
            <Card data-testid="sync-runs-card">
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-sync-run-type">{i18n.inboundConnectors.syncRunType}</TableHead>
                                <TableHead data-testid="col-sync-run-status">{i18n.inboundConnectors.syncRunStatus}</TableHead>
                                <TableHead data-testid="col-sync-run-started">{i18n.inboundConnectors.syncRunStarted}</TableHead>
                                <TableHead data-testid="col-sync-run-duration">{i18n.inboundConnectors.syncRunDuration}</TableHead>
                                <TableHead data-testid="col-sync-run-created">{i18n.inboundConnectors.syncRunCreated}</TableHead>
                                <TableHead data-testid="col-sync-run-updated">{i18n.inboundConnectors.syncRunUpdated}</TableHead>
                                <TableHead data-testid="col-sync-run-skipped">{i18n.inboundConnectors.syncRunSkipped}</TableHead>
                                <TableHead data-testid="col-sync-run-errored">{i18n.inboundConnectors.syncRunErrored}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {runs.map(run => (
                                <SyncRunRow
                                    key={run.syncRunId}
                                    connectorId={connectorId}
                                    run={run}
                                    i18n={i18n}
                                    canApproveSync={canApproveSync}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
            {runsPage && runsPage.totalPages > 1 && (
                <Pagination
                    page={runsPage.number}
                    totalPages={runsPage.totalPages}
                    totalItems={runsPage.totalElements}
                    pageSize={pageSize}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={[10, 20, 50]}
                />
            )}
        </Stack>
    )
}

function SyncRunRow({connectorId, run, i18n, canApproveSync}: {
    connectorId: string
    run: InboundSyncRun
    i18n: ReturnType<typeof useI18n>
    canApproveSync: boolean
}) {
    const [expanded, setExpanded] = useState(false)
    const approveDialog = useDialog()
    const rejectDialog = useDialog()
    const [recordsPage, setRecordsPage] = useState(0)
    const [recordsPageSize, setRecordsPageSize] = useState(20)
    const [actionFilter, setActionFilter] = useState<string>(RECORDS_FILTER_ALL)

    const approveMutation = useApprovePausedSync(connectorId)
    const rejectMutation = useRejectPausedSync(connectorId)

    const isPaused = run.status === SyncRunStatus.PAUSED

    const duration = run.completedAt && run.startedAt
        ? Math.round((AppDate.epochMillis(run.completedAt) - AppDate.epochMillis(run.startedAt)) / 1000)
        : null

    /* eslint-disable i18next/no-literal-string -- query params */
    const actionParam = actionFilter !== RECORDS_FILTER_ALL ? `&action=${actionFilter}` : ''
    const recordsQuery = expanded ? `page=${recordsPage}&size=${recordsPageSize}${actionParam}` : ''
    /* eslint-enable i18next/no-literal-string */

    const {data: recordsData, isLoading: recordsLoading} = useSyncRecords(
        expanded ? connectorId : undefined,
        expanded ? run.syncRunId : undefined,
        recordsQuery,
    )
    const records = recordsData?.content ?? []

    // WHY: paginated list response carries empty errorLogIds (issue 566 — avoid N+1 across page);
    // run-detail is the only endpoint that materialises the per-record refs.
    const {data: runDetail} = useSyncRunDetail(
        expanded ? connectorId : undefined,
        expanded ? run.syncRunId : undefined,
    )
    const errorLogRefs = runDetail?.errorLogIds ?? []

    const handleApprove = () => {
        approveMutation.mutate(run.syncRunId, {
            onSuccess: () => approveDialog.close(),
        })
    }
    const handleReject = () => {
        rejectMutation.mutate(run.syncRunId, {
            onSuccess: () => rejectDialog.close(),
        })
    }

    return (
        <>
            <ClickableTableRow
                onClick={() => setExpanded(!expanded)}
                data-testid={`sync-run-row-${run.syncRunId}`}
            >
                <TableCell>
                    <Badge variant="outline">{syncTypeLabel(run.syncType, i18n)}</Badge>
                </TableCell>
                <TableCell>
                    <SyncStatusBadge status={run.status}/>
                </TableCell>
                <TableCell>{formatTimestamp(run.startedAt)}</TableCell>
                <TableCell>{duration !== null ? i18n.inboundConnectors.syncRunDurationValue({seconds: duration}) : '—'}</TableCell>
                <TableCell>{run.recordsCreated}</TableCell>
                <TableCell>{run.recordsUpdated}</TableCell>
                <TableCell>{run.recordsSkipped}</TableCell>
                <TableCell>{run.recordsErrored}</TableCell>
            </ClickableTableRow>
            {expanded && (
                // read-only
                <TableRow data-readonly>
                    <TableCell colSpan={8} className="bg-muted/30">
                        <Stack className="p-4" data-testid="sync-run-detail">
                            <DetailGrid columns={4}>
                                <StatCard
                                    label={i18n.inboundConnectors.syncRunCreated}
                                    value={run.recordsCreated}
                                />
                                <StatCard
                                    label={i18n.inboundConnectors.syncRunUpdated}
                                    value={run.recordsUpdated}
                                />
                                <StatCard
                                    label={i18n.inboundConnectors.syncRunSkipped}
                                    value={run.recordsSkipped}
                                />
                                <StatCard
                                    label={i18n.inboundConnectors.syncRunErrored}
                                    value={run.recordsErrored}
                                />
                                {run.errorMessage && (
                                    <DetailField
                                        label={String(i18n.inboundConnectors.syncRunErrorMessage)}
                                        value={<ErrorText className="font-mono">{run.errorMessage}</ErrorText>}
                                        colSpanClass="col-span-full"
                                    />
                                )}
                                <DetailField
                                    label={String(i18n.inboundConnectors.syncRunTriggeredBy)}
                                    value={<SmallText>{run.triggeredByUser || run.triggeredBy}</SmallText>}
                                />
                            </DetailGrid>

                            {isPaused && canApproveSync && (
                                <InlineRow gap="sm" data-testid={`paused-actions-${run.syncRunId}`}>
                                    <Button
                                        data-testid={`btn-approve-${run.syncRunId}`}
                                        variant="default"
                                        onClick={approveDialog.open}
                                        disabled={approveMutation.isPending || rejectMutation.isPending}
                                    >
                                        {approveMutation.isPending ? <ButtonSpinner/> : <Icon icon={Check} className="mr-2"/>}
                                        {i18n.inboundConnectors.syncRunApprove}
                                    </Button>
                                    <Button
                                        data-testid={`btn-reject-${run.syncRunId}`}
                                        variant="destructive"
                                        onClick={rejectDialog.open}
                                        disabled={approveMutation.isPending || rejectMutation.isPending}
                                    >
                                        {rejectMutation.isPending ? <ButtonSpinner/> : <Icon icon={X} className="mr-2"/>}
                                        {i18n.inboundConnectors.syncRunReject}
                                    </Button>
                                </InlineRow>
                            )}

                            <Card data-testid={`sync-records-card-${run.syncRunId}`}>
                                <CardHeader>
                                    <InlineRow gap="md" justify="between">
                                        <CardTitle data-testid="card-title-sync-records">{i18n.inboundConnectors.syncRunRecordsTitle}</CardTitle>
                                        <Select
                                            value={actionFilter}
                                            onValueChange={(value) => {
                                                setActionFilter(value)
                                                setRecordsPage(0)
                                            }}
                                        >
                                            <SelectTrigger data-testid={`sync-records-filter-${run.syncRunId}`} className="w-48">
                                                <SelectValue/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value={RECORDS_FILTER_ALL}>{i18n.inboundConnectors.syncRunRecordsFilterAll}</SelectItem>
                                                <SelectItem value={SyncRecordAction.CREATE}>{i18n.inboundConnectors.syncRunCreated}</SelectItem>
                                                <SelectItem value={SyncRecordAction.UPDATE}>{i18n.inboundConnectors.syncRunUpdated}</SelectItem>
                                                <SelectItem value={SyncRecordAction.DELETE}>{SyncRecordAction.DELETE}</SelectItem>
                                                <SelectItem value={SyncRecordAction.SKIP}>{i18n.inboundConnectors.syncRunSkipped}</SelectItem>
                                                <SelectItem value={SyncRecordAction.ERROR}>{i18n.inboundConnectors.syncRunErrored}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </InlineRow>
                                </CardHeader>
                                <CardContent className="p-0">
                                    {recordsLoading ? (
                                        <MutedText className="p-4">{i18n.common.loading}</MutedText>
                                    ) : records.length === 0 ? (
                                        <EmptyState
                                            icon={Eye}
                                            title={i18n.inboundConnectors.syncRunRecordsEmpty}
                                            data-testid={`sync-records-empty-${run.syncRunId}`}
                                        />
                                    ) : (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead data-testid="col-sync-record-external-id">{i18n.inboundConnectors.syncRecordExternalId}</TableHead>
                                                    <TableHead data-testid="col-sync-record-entity-type">{i18n.inboundConnectors.syncRecordEntityType}</TableHead>
                                                    <TableHead data-testid="col-sync-record-action">{i18n.inboundConnectors.syncRecordAction}</TableHead>
                                                    <TableHead data-testid="col-sync-record-correlated-identity">{i18n.inboundConnectors.syncRecordCorrelatedIdentity}</TableHead>
                                                    <TableHead data-testid="col-sync-record-processed-at">{i18n.inboundConnectors.syncRecordProcessedAt}</TableHead>
                                                    <TableHead data-testid="col-sync-record-error-message">{i18n.inboundConnectors.syncRecordErrorMessage}</TableHead>
                                                    <TableHead data-testid="col-sync-record-payload"/>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {records.map((record) => (
                                                    <SyncRecordRow key={record.id} record={record}/>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </CardContent>
                            </Card>

                            {recordsData && recordsData.totalPages > 1 && (
                                <Pagination
                                    page={recordsData.number}
                                    totalPages={recordsData.totalPages}
                                    totalItems={recordsData.totalElements}
                                    pageSize={recordsPageSize}
                                    onPageChange={setRecordsPage}
                                    onPageSizeChange={(size) => {
                                        setRecordsPageSize(size)
                                        setRecordsPage(0)
                                    }}
                                    pageSizeOptions={[10, 20, 50]}
                                />
                            )}

                            <ErrorLogRefsCard runId={run.syncRunId} errorLogRefs={errorLogRefs} i18n={i18n}/>
                        </Stack>
                    </TableCell>
                </TableRow>
            )}

            {canApproveSync && (<>
            <Dialog open={approveDialog.isOpen} onOpenChange={approveDialog.bind.onOpenChange}>
                <DialogContent data-testid={`approve-dialog-${run.syncRunId}`}>
                    <DialogHeader>
                        <DialogTitle>{i18n.inboundConnectors.syncRunApproveTitle}</DialogTitle>
                        <DialogDescription>{i18n.inboundConnectors.syncRunApproveBody}</DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="approve"
                        onCancel={approveDialog.close}
                        saving={approveMutation.isPending}
                        saveLabel={i18n.inboundConnectors.syncRunApprove}
                        saveType="button"
                        onSave={handleApprove}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={rejectDialog.isOpen} onOpenChange={rejectDialog.bind.onOpenChange}>
                <DialogContent data-testid={`reject-dialog-${run.syncRunId}`}>
                    <DialogHeader>
                        <DialogTitle>{i18n.inboundConnectors.syncRunRejectTitle}</DialogTitle>
                        <DialogDescription>{i18n.inboundConnectors.syncRunRejectBody}</DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="reject"
                        onCancel={rejectDialog.close}
                        saving={rejectMutation.isPending}
                        saveLabel={i18n.inboundConnectors.syncRunReject}
                        destructive
                        saveType="button"
                        onSave={handleReject}
                    />
                </DialogContent>
            </Dialog>
            </>)}
        </>
    )
}

function ErrorLogRefsCard({
    runId,
    errorLogRefs,
    i18n,
}: {
    runId: string
    errorLogRefs: ErrorLogRef[]
    i18n: ReturnType<typeof useI18n>
}) {
    const count = errorLogRefs.length
    return (
        <Card data-testid={`sync-run-error-logs-card-${runId}`}>
            <CardHeader>
                <InlineRow gap="md" justify="between">
                    <CardTitle data-testid="card-title-sync-error-logs">
                        {i18n.inboundConnectors.syncRunErrorLogsTitle}
                    </CardTitle>
                    <Badge variant="outline" data-testid={`sync-run-error-logs-count-${runId}`}>
                        {i18n.inboundConnectors.syncRunErrorLogsCountLabel({count})}
                    </Badge>
                </InlineRow>
            </CardHeader>
            <CardContent className="p-0">
                {count === 0 ? (
                    <MutedText className="p-4" data-testid={`sync-run-error-logs-empty-${runId}`}>
                        {i18n.inboundConnectors.syncRunErrorLogsEmpty}
                    </MutedText>
                ) : (
                    <Stack className="p-4" gap="sm">
                        <SmallText>{i18n.inboundConnectors.syncRunErrorLogsHint}</SmallText>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-error-log-id">
                                        {i18n.inboundConnectors.syncRunErrorLogIdHeader}
                                    </TableHead>
                                    <TableHead data-testid="col-error-log-created-at">
                                        {i18n.inboundConnectors.syncRunErrorLogCreatedAtHeader}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {errorLogRefs.map(ref => (
                                    // read-only
                                    <TableRow
                                        key={ref.errorLogId}
                                        data-testid={`error-log-ref-${ref.errorLogId}`}
                                        data-readonly
                                    >
                                        <TableCell>
                                            <TextLink
                                                to={routes.admin.errorLogs.detail(ref.errorLogId)}
                                                testId={`error-log-link-${ref.errorLogId}`}
                                                stopPropagation
                                            >
                                                <CodeText>{ref.errorLogId}</CodeText>
                                            </TextLink>
                                        </TableCell>
                                        <TableCell>{formatTimestamp(ref.errorLogCreatedAt)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Stack>
                )}
            </CardContent>
        </Card>
    )
}

function SyncRecordRow({record}: {record: InboundSyncRecord}) {
    const i18n = useI18n()
    const [showPayload, setShowPayload] = useState(false)
    return (
        <>
            {/* read-only */}
            <TableRow data-testid={`sync-record-row-${record.id}`} data-readonly>
                <TableCell><CodeText>{record.externalId}</CodeText></TableCell>
                <TableCell>{record.entityType}</TableCell>
                <TableCell><SyncRecordActionBadge action={record.action}/></TableCell>
                <TableCell>
                    {record.correlatedIdentityId ? (
                        <TextLink
                            to={routes.identityManagement.identities.detail(record.correlatedIdentityId)}
                            testId={`sync-record-identity-${record.id}`}
                            stopPropagation
                        >
                            {record.correlatedIdentityId}
                        </TextLink>
                    ) : (
                        <MutedText>{'—'}</MutedText>
                    )}
                </TableCell>
                <TableCell>{record.processedAt ? formatTimestamp(record.processedAt) : '—'}</TableCell>
                <TableCell>
                    <SyncRecordErrorCell errorMessage={record.errorMessage} i18n={i18n}/>
                </TableCell>
                <TableCell>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPayload(prev => !prev)}
                        data-testid={`btn-toggle-payload-${record.id}`}
                        aria-label={showPayload
                            ? String(i18n.inboundConnectors.syncRecordHidePayload)
                            : String(i18n.inboundConnectors.syncRecordShowPayload)}
                    >
                        <Icon icon={Eye}/>
                    </Button>
                </TableCell>
            </TableRow>
            {showPayload && (
                // read-only
                <TableRow data-readonly>
                    <TableCell colSpan={7}>
                        <SyncRecordPayloadDetail record={record} i18n={i18n}/>
                    </TableCell>
                </TableRow>
            )}
        </>
    )
}

function SyncRecordPayloadDetail({
    record,
    i18n,
}: {
    record: InboundSyncRecord
    i18n: ReturnType<typeof useI18n>
}) {
    return (
        <Stack gap="sm" className="p-2" data-testid={`sync-record-payload-${record.id}`}>
            <PayloadField
                label={String(i18n.inboundConnectors.syncRecordRawData)}
                value={record.rawData}
                processedAt={record.processedAt}
                i18n={i18n}
                testId={`raw-${record.id}`}
            />
            <PayloadField
                label={String(i18n.inboundConnectors.syncRecordMappedData)}
                value={record.mappedData}
                processedAt={record.processedAt}
                i18n={i18n}
                testId={`mapped-${record.id}`}
            />
        </Stack>
    )
}

function PayloadField({
    label,
    value,
    processedAt,
    i18n,
    testId,
}: {
    label: string
    value: Record<string, unknown> | null | undefined
    processedAt: string | null
    i18n: ReturnType<typeof useI18n>
    testId: string
}) {
    if (value && Object.keys(value).length > 0) {
        return (
            <div>
                <MutedText size="sm" weight="medium">{label}</MutedText>
                <pre data-testid={testId} className="mt-1">
                    <CodeText>{JSON.stringify(value, null, 2)}</CodeText>
                </pre>
            </div>
        )
    }
    const placeholder = processedAt
        ? i18n.inboundConnectors.syncRecordPayloadCleared
        : i18n.inboundConnectors.syncRecordPayloadPreProcessing
    return (
        <div>
            <MutedText size="sm" weight="medium">{label}</MutedText>
            <MutedText size="sm" data-testid={testId}>{placeholder}</MutedText>
        </div>
    )
}

function SyncRecordErrorCell({
    errorMessage,
    i18n,
}: {
    errorMessage: string | null
    i18n: ReturnType<typeof useI18n>
}) {
    if (!errorMessage) {
        return <MutedText>{'—'}</MutedText>
    }
    const colon = errorMessage.indexOf(':')
    if (colon > 0) {
        const code = errorMessage.slice(0, colon)
        const detail = errorMessage.slice(colon + 1).trim()
        const codeLabels = i18n.inboundConnectors.errorCode as Record<string, string | undefined>
        const label = codeLabels[code]
        if (label) {
            return (
                <div>
                    <Badge variant={BadgeVariant.DESTRUCTIVE}>{label}</Badge>
                    <ErrorText className="font-mono mt-1">{detail}</ErrorText>
                </div>
            )
        }
    }
    return <ErrorText className="font-mono">{errorMessage}</ErrorText>
}

function SyncRecordActionBadge({action}: {action: string | null}) {
    if (!action) return <MutedText>{'—'}</MutedText>
    switch (action) {
        case SyncRecordAction.CREATE:
            return <Badge variant={BadgeVariant.SUCCESS}>{action}</Badge>
        case SyncRecordAction.UPDATE:
            return <Badge variant="secondary">{action}</Badge>
        case SyncRecordAction.DELETE:
        case SyncRecordAction.ERROR:
            return <Badge variant="destructive">{action}</Badge>
        case SyncRecordAction.SKIP:
            return <Badge variant="outline">{action}</Badge>
        default:
            return <Badge variant="secondary">{action}</Badge>
    }
}

export function SyncStatusBadge({status}: {status: string}) {
    const i18n = useI18n()

    switch (status) {
        case SyncRunStatus.RUNNING:
            return (
                <Badge variant="secondary" className="gap-1">
                    <Icon icon={Loader2} size="xs" className="animate-spin"/>
                    {i18n.inboundConnectors.syncStatusRunning}
                </Badge>
            )
        case SyncRunStatus.COMPLETED:
            return (
                <Badge variant={BadgeVariant.SUCCESS} className="gap-1">
                    <Check className="h-3 w-3"/>
                    {i18n.inboundConnectors.syncStatusCompleted}
                </Badge>
            )
        case SyncRunStatus.COMPLETED_WITH_ERRORS:
            return (
                <Badge variant="warning" className="gap-1">
                    <AlertTriangle className="h-3 w-3"/>
                    {i18n.inboundConnectors.syncStatusCompletedWithErrors}
                </Badge>
            )
        case SyncRunStatus.FAILED:
            return (
                <Badge variant="destructive" className="gap-1">
                    <X className="h-3 w-3"/>
                    {i18n.inboundConnectors.syncStatusFailed}
                </Badge>
            )
        case SyncRunStatus.CANCELLED:
            return (
                <Badge variant="outline" className="gap-1">
                    <X className="h-3 w-3"/>
                    {i18n.inboundConnectors.syncStatusCancelled}
                </Badge>
            )
        case SyncRunStatus.PAUSED:
            return (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Badge variant="warning" className="gap-1">
                                <AlertTriangle className="h-3 w-3"/>
                                {i18n.inboundConnectors.syncStatusPaused}
                            </Badge>
                        </TooltipTrigger>
                        <TooltipContent>{i18n.inboundConnectors.pausedTooltip}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )
        default:
            return <Badge variant="secondary">{status}</Badge>
    }
}

function syncTypeLabel(type: string, i18n: ReturnType<typeof useI18n>): string {
    switch (type) {
        case SyncType.FULL:
            return i18n.inboundConnectors.syncTypeFull
        case SyncType.PREVIEW:
            return i18n.inboundConnectors.syncTypePreview
        default:
            return type
    }
}

function parsePositiveInt(value: string | null, fallback: number): number {
    if (!value) return fallback
    const parsed = Number.parseInt(value, 10)
    if (Number.isNaN(parsed) || parsed < 0) return fallback
    return parsed
}
